import type { ReactElement } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Box, Button, Stack } from '@mantine/core';
import { FileCheck, Settings } from 'lucide-react';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import SideRailPageLayout from '@/shared/surfaces/SideRailPageLayout';
import TopicSummaryPanel from '@/shared/surfaces/TopicSummaryPanel';
import DerivedInsightsPanel from '@/shared/surfaces/DerivedInsightsPanel';
import { useSurface } from '@/shared/surfaces/useSurface';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import type { TopicCallout } from '@/shared/surfaces/TopicNarration';
import type { GovernanceOutletContext } from './GovernanceLayout';
import PolicyRegister, { postureStats } from './PolicyRegister';
import { formatPct } from './PolicyFormatting';
import type { GovernancePosture } from './PolicyTypes';

const MANAGE_ROUTE = '/classification/governance';

function policyCallouts(posture: GovernancePosture): TopicCallout[] {
    const out: TopicCallout[] = [];
    if (posture.policyCount > 0) {
        out.push({ tone: 'headline', text: `${posture.policyCount} active ${posture.policyCount === 1 ? 'policy' : 'policies'} tracking ${posture.totalTurns.toLocaleString('en-US')} classified prompts.` });
    }
    if (posture.flaggedFindings > 0) {
        out.push({ tone: 'watch', text: `${posture.flaggedFindings.toLocaleString('en-US')} prompts carry a sensitivity flag — triage them on the Issues screen.` });
    }
    if (posture.avgConfidence !== null) {
        out.push({ tone: 'opportunity', text: `Classifier confidence averages ${formatPct(posture.avgConfidence)} across governance verdicts.` });
    }
    return out;
}

/** Governance › Policies: the policy register beside a posture summary + insights rail. */
export default function GovernancePoliciesPage(): ReactElement {
    const surface = useSurface('governance');
    const { registerVm } = useOutletContext<GovernanceOutletContext>();
    if (surface === undefined) return <TopicNotFound />;

    return (
        <SurfaceAssistScope surface={surface}>
            <Box pb="xl">
                <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

                <PageHeaderComponent
                    icon={FileCheck}
                    eyebrow="Governance"
                    title="Policies"
                    subtitle="Your governance policies and how the org is holding to them — each one's real classified activity and top verdict."
                    rightToolbar={
                        <Button component={Link} to={MANAGE_ROUTE} variant="default" size="xs" leftSection={<Settings size={14} />}>
                            Manage policies
                        </Button>
                    }
                />

                <SideRailPageLayout
                    rail={
                        <Stack gap="md">
                            <TopicSummaryPanel stats={postureStats(registerVm.posture)} />
                            <DerivedInsightsPanel callouts={policyCallouts(registerVm.posture)} accent="indigo" />
                        </Stack>
                    }
                >
                    <PolicyRegister vm={registerVm} />
                </SideRailPageLayout>
            </Box>
        </SurfaceAssistScope>
    );
}
